import React, { useEffect } from 'react';
import _ from 'underscore';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { Button, Card, CardContent, CardActions, Grid, Divider, TextField, colors } from '@mui/material';
import { SelectField } from 'components';
import DatePicker from 'components/DatePicker/DatePicker';
import validate from 'validate.js';

import { ReportSunsetExport, SetFormState } from '../../actions/ReportSunsetActions';

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3),
	},
	container: {
		marginTop: theme.spacing(3),
	},
	tabs: {
		marginTop: theme.spacing(3),
	},
	content: {
		marginTop: theme.spacing(3),
	},
	generate: {
		color: theme.palette.white,
		backgroundColor: colors.green[600],
		'&:hover': {
			backgroundColor: colors.green[900],
		},
	},
}));

const schema = {
	site: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	Start_Period: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	End_Period: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	tech: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	vendor: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	type: {
		presence: { allowEmpty: false, message: 'is required' },
	}
};

const vendor = [
	{ value: 'All', text: 'All' },
	{ value: 'Nokia', text: 'Nokia' },
	{ value: 'Huawei', text: 'Huawei' },
	{ value: 'Ericsson', text: 'Ericsson' }
];

const tech = [
	{ value: 'All', text: 'All' },
	{ value: '2G', text: '2G' },
	{ value: '4G', text: '4G' },
	{ value: '5G', text: '5G' }
];

const type = [
	{ value: 'All', text: 'All' },
	{ value: 'Site', text: 'Site' },
	{ value: 'Cell', text: 'Cell' }
];

const ReportSunsetForm = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.reportSunsetReducer.formState);

	useEffect(() => {
		const errors = validate(formState.values, schema);

		setFormState({
			...formState,
			isValid: !errors,
			errors: errors || {},
		});
	}, [formState.values]);

	const _handleChange = (event) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value,
			},
			touched: {
				...formState.touched,
				[event.target.name]: true,
			},
		});
	};

	const _handleDateChange = (value, name) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[name]: value,
			},
			touched: {
				...formState.touched,
				[name]: true,
			},
		});
	};

	const _hasError = (field) => (!!(formState.touched[field] && formState.errors[field]));
	const user = useSelector(state => state.session.user);
	const _generate = () => {
		dispatch(ReportSunsetExport(formState.values, user.id));
	};

	return (
		<Card className={classes.content}>
			<CardContent>
				<Grid container spacing={1}>
					<Grid item md={12} xs={12}>
						<TextField
							fullWidth
							label="Site(s) List"
							name="site"
							onChange={_handleChange}
							value={formState.values.site}
							variant="outlined"
							error={_hasError('site')}
							helperText={
								_hasError('site') ? formState.errors.site[0] : 'Example: 10500,10501,10502'
							}
						/>
					</Grid>
				</Grid>
				<br />
				<Grid container spacing={1}>
					<Grid item md={3} xs={12}>
						<DatePicker
							label="Start Period Date"
							name="Start_Period"
							value={formState.values.Start_Period || null}
							onChange={(value) => _handleDateChange(value, 'Start_Period')}
							error={_hasError('Start_Period')}
							helperText={_hasError('Start_Period') ? formState.errors.Start_Period[0] : null}
						/>
					</Grid>
					<Grid item md={3} xs={12}>
						<DatePicker
							label="End Period Date"
							name="End_Period"
							value={formState.values.End_Period || null}
							onChange={(value) => _handleDateChange(value, 'End_Period')}
							error={_hasError('End_Period')}
							helperText={_hasError('End_Period') ? formState.errors.End_Period[0] : null}
						/>
					</Grid>
					<Grid item md={2} xs={12}>
						<SelectField
							id="type"
							name="type"
							value={formState.values.type || ''}
							onChange={_handleChange}
							hintText="Type"
							menuItems={type}
							error={_hasError('type')}
							helperText={_hasError('type') ? formState.errors.type[0] : null}
						/>
					</Grid>
					<Grid item md={2} xs={12}>
						<SelectField
							id="tech"
							name="tech"
							value={formState.values.tech || ''}
							onChange={_handleChange}
							hintText="Tech"
							menuItems={tech}
							error={_hasError('tech')}
							helperText={_hasError('tech') ? formState.errors.tech[0] : null}
						/>
					</Grid>
					<Grid item md={2} xs={12}>
						<SelectField
							id="vendor"
							name="vendor"
							value={formState.values.vendor || ''}
							onChange={_handleChange}
							hintText="Vendor"
							menuItems={vendor}
							error={_hasError('vendor')}
							helperText={_hasError('vendor') ? formState.errors.vendor[0] : null}
						/>
					</Grid>
				</Grid>
			</CardContent>
			<Divider />
			<CardActions>
				<Button className={classes.generate} onClick={_generate} variant="contained" disabled={!formState.isValid}>Export</Button>
			</CardActions>
		</Card>
	);
};

export default ReportSunsetForm;
